/**
 * InfluencerCampaigns.jsx — list of influencer campaigns with a create dialog.
 * Simple grid of campaign cards linking to the detail page.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Sparkles, Plus, Loader2, Users2, TrendingUp, Search } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";

const STATUS_COLORS = {
  draft:      "bg-neutral-100 text-neutral-700 border-neutral-300",
  planning:   "bg-sky-50 text-sky-700 border-sky-300",
  active:     "bg-emerald-50 text-emerald-700 border-emerald-300",
  in_review:  "bg-amber-50 text-amber-700 border-amber-300",
  completed:  "bg-primary/10 text-primary border-primary/30",
  archived:   "bg-slate-100 text-slate-500 border-slate-200",
  cancelled:  "bg-rose-50 text-rose-700 border-rose-300",
};

export default function InfluencerCampaigns() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      params.set("limit", "100");
      const { data } = await api.get(`/workspaces/${wsId}/influencer-campaigns?${params}`);
      setRows(data?.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    finally { setLoading(false); }
  }, [wsId, q, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="inf-campaigns-page">
      <PageHeader
        title="Campaigns"
        icon={TrendingUp}
        description="Every influencer campaign — planning, active, or completed."
        actions={(
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/app/influencers" data-testid="inf-back-directory"><Users2 className="w-3.5 h-3.5 mr-1.5" /> Directory</Link>
            </Button>
            <Button size="sm" onClick={() => setShowNew(true)} data-testid="inf-camp-new">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New campaign
            </Button>
          </div>
        )}
      />

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-9 text-xs pl-8"
                 placeholder="Search name or code…" data-testid="inf-camp-search" />
        </div>
        <Select value={status || "__all__"} onValueChange={(v) => setStatus(v === "__all__" ? "" : v)}>
          <SelectTrigger className="h-9 text-xs w-40" data-testid="inf-camp-status"><SelectValue placeholder="Any status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any status</SelectItem>
            {["draft", "planning", "active", "in_review", "completed", "archived", "cancelled"].map((s) =>
              <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="p-6 text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={TrendingUp} title="No campaigns yet"
                    description="Create your first campaign to start inviting creators." testId="inf-camp-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="inf-camp-grid">
          {rows.map((c) => (
            <Link to={`/app/influencers/campaigns/${c.id}`} key={c.id}
                  className="block border border-border p-4 hover:border-primary transition group" data-testid={`inf-camp-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">{c.code}</div>
                  <div className="font-semibold text-sm truncate group-hover:text-primary">{c.name}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 border ${STATUS_COLORS[c.status] || STATUS_COLORS.draft}`}>
                  {c.status?.replace("_", " ")}
                </span>
              </div>
              {c.brand?.name && (
                <div className="text-[11px] text-muted-foreground mt-1 truncate">{c.brand.name}</div>
              )}
              <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
                <div>
                  <div className="text-[9px] uppercase text-muted-foreground">Budget</div>
                  <div className="font-mono">₹{((c.budget_total_inr || 0) / 1000).toFixed(0)}K</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase text-muted-foreground">Creators</div>
                  <div className="font-mono">{c.participant_count || 0}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase text-muted-foreground">Timeline</div>
                  <div className="font-mono truncate">{c.start_date?.slice(5) || "—"}{c.end_date ? ` → ${c.end_date.slice(5)}` : ""}</div>
                </div>
              </div>
              {c.brief && (
                <div className="text-[10px] text-muted-foreground mt-3 line-clamp-2 border-t border-border pt-2">{c.brief}</div>
              )}
              <div className="text-[9px] text-muted-foreground mt-2">Created {fmtRelative(c.created_at)}</div>
            </Link>
          ))}
        </div>
      )}

      {showNew && <NewCampaignDialog wsId={wsId} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewCampaignDialog({ wsId, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [brief, setBrief] = useState("");
  const [brandId, setBrandId] = useState("");
  const [brands, setBrands] = useState([]);
  const [budget, setBudget] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [deliverables, setDeliverables] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/workspaces/${wsId}/brands`).then((r) => setBrands(r.data || [])).catch(() => {});
  }, [wsId]);

  const toggleDeliv = (t) => setDeliverables((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);

  const save = async () => {
    if (!name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/influencer-campaigns`, {
        name, brief: brief || undefined,
        brand_id: brandId || undefined,
        budget_total_inr: Number(budget) || 0,
        start_date: start || undefined, end_date: end || undefined,
        hashtags: hashtags.split(",").map((s) => s.trim().replace(/^#/, "")).filter(Boolean),
        deliverable_types: deliverables,
        status: "planning",
      });
      toast.success(`Created ${data.code}`);
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="inf-camp-new-dialog">
        <DialogHeader><DialogTitle>New influencer campaign</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-[10px] uppercase">Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 text-xs" data-testid="inf-camp-new-name" /></div>
          <div>
            <Label className="text-[10px] uppercase">Brand</Label>
            <Select value={brandId || "__none__"} onValueChange={(v) => setBrandId(v === "__none__" ? "" : v)}>
              <SelectTrigger className="h-9 text-xs" data-testid="inf-camp-new-brand"><SelectValue placeholder="No brand" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No brand</SelectItem>
                {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-[10px] uppercase">Brief</Label>
            <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={3}
                      className="w-full text-xs px-2 py-1 bg-background border border-border resize-y"
                      placeholder="One-liner or paragraph describing the campaign goals…" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-[10px] uppercase">Budget (₹)</Label><Input type="number" value={budget} onChange={(e) => setBudget(e.target.value)} className="h-9 text-xs" /></div>
            <div><Label className="text-[10px] uppercase">Start</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 text-xs" /></div>
            <div><Label className="text-[10px] uppercase">End</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 text-xs" /></div>
          </div>
          <div>
            <Label className="text-[10px] uppercase">Deliverable types</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {["reel", "post", "story", "video", "live", "blog", "tweet"].map((t) => {
                const on = deliverables.includes(t);
                return (
                  <button key={t} type="button" onClick={() => toggleDeliv(t)}
                          className={`text-[10px] px-2 py-1 border ${on ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"}`}>
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
          <div><Label className="text-[10px] uppercase">Hashtags (comma-sep)</Label>
            <Input value={hashtags} onChange={(e) => setHashtags(e.target.value)} className="h-9 text-xs" placeholder="SpringCoffee, AcmeLaunch" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="inf-camp-new-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
